/* eslint-disable */

import {
  ContentBlock,
  ContentState,
  DefaultDraftInlineStyle,
  DraftInlineStyle,
  DraftStyleMap,
} from "draft-js";
import { Map, OrderedSet } from "immutable";
import { camelCase, isEmpty, kebabCase } from "lodash";
import { CSSProperties } from "react";

import { fontFamilyOptions, fontSizeOptions } from "@/models/editors/editor";
import { getCorrectUrl } from "@/utils/common";

// Constants for space handling
const FULLWIDTH_SPACE_CHAR = "\u3000";
const FULLWIDTH_SPACE_HTML_ENTITY = "&#12288;";
const HALFWIDTH_SPACE = " ";
const NON_BREAKING_SPACE_HTML_ENTITY = "&nbsp;";
const ENTITY_TYPE_LINK = "LINK";

/**
 * blockRenderMap, customStyleMap, customStyleHandler & getBlockRendererFn are used by draft.js to convert its internal data structure
 * into html for display in the editor's content-editable area.
 */
export const blockRenderMap = {
  unstyled: {
    element: "div",
  },
};

export const customStyleMap = (() => {
  const styleMap = { ...DefaultDraftInlineStyle } as DraftStyleMap;
  fontFamilyOptions.forEach((opt) => {
    styleMap[`fontFamily.${opt.value}`] = { fontFamily: opt.value };
  });
  fontSizeOptions.forEach((opt) => {
    styleMap[`fontSize.${opt.value}`] = { fontSize: `${opt.value}` };
  });
  // リンク設定の文字色
  // TODO:リンク設定の装飾について、確認次第適用
  styleMap["insertLink"] = { color: "#0088FF", textDecoration: "underline" };
  return styleMap;
})();

/**
 * DraftInlineStyleデータにkeyとして設定したものから、スタイル設定に変換する
 * @param style
 * @returns
 */
export const customStyleHandler = (
  style: DraftInlineStyle,
): DraftInlineStyle => {
  // draftJSのデフォルトセットと、customStyleMapで定義したスタイルを抽出(リンク箇所等)
  const defaultStyles = style
    .intersect(["BOLD", "CODE", "ITALIC", "UNDERLINE", "insertLink"])
    .reduce((map, v) => {
      if (map === undefined || v === undefined) {
        return Map();
      }
      return map.merge(Map(customStyleMap[v]));
    }, Map());

  style = style.subtract(["BOLD", "CODE", "ITALIC", "UNDERLINE", "insertLink"]);

  // separate out any entries that are a string of multiple styles
  let groupedStyles: any = style.filter((v) => (!v ? false : v.includes(":")));
  style = style.subtract(groupedStyles);

  // DraftInlineStyleでの形式から、CSSの形式に変換
  groupedStyles = groupedStyles.reduce(
    (map: Map<unknown, unknown>, v: string) => {
      if (map === undefined || v === undefined) {
        return Map();
      }
      v = String(convertStyleStringToObject(v));
      v = String(Map(v).mapKeys((k) => camelCase(String(k))));
      return map.merge(Map(v));
    },
    Map(),
  );

  // 「.」で区切ったスタイル設定を、CSSの形式に変換
  style = style
    .map((v) => (!v ? [] : v.split(".")))
    .filter((v) => (!v ? false : v.every((vv: string | any[]) => vv.length)))
    .reduce((map, v) => {
      if (map === undefined || !v) {
        return Map();
      }
      const key = String(v.shift()?.trim());
      const val = v.join(".").trim();
      return map.merge({ [key]: val });
    }, groupedStyles.merge(defaultStyles))
    .toJS();

  if (isEmpty(style)) {
    return OrderedSet();
  }
  return style;
};

/**
 * DraftInlineStyleとして取得したものをCSSPropertiesの形式で返す
 * @param style
 * @returns
 */
export const customStyleFn = (style: DraftInlineStyle) => {
  return customStyleHandler(style) as unknown as CSSProperties;
};

// helper function converts style attribute string into key-value pairs
function convertStyleStringToObject(style = "", data = {}) {
  if (!style) {
    return null;
  }
  return style
    .split(";")
    .filter((s) => s.includes(":"))
    .map((s) => s.split(":"))
    .reduce((map: { [index: string]: string }, s) => {
      const key = s.shift()?.trim();
      if (key) {
        const val = s.join(":").trim();
        map[key] = val;
      }
      return map;
    }, data);
}

/**
 * function receives contentState from a draft.js instance and returns a set of helper functions
 * used to convert draft.js internal state into html for export outside of draft.js.
 */
export const getStateToHtmlOptions = (
  contentState: ContentState,
  classes?: string,
  styles?: string,
) => ({
  /**
   * NOTE: the rich text editor relies on the following styles for ordered lists. For ordered list numbering to display correctly
   * these styles should be included in the style tag or style sheet of any document that includes content from the rich text editor:
   * .ordered-list-item:before { left: -36px; position: absolute; text-align: right; width: 30px; }
   * .ordered-list-item:before { content: counter(ol0) ". "; counter-increment: ol0; }
   * .ordered-list-item.depth1:before { content: counter(ol1, lower-alpha) ") "; counter-increment: ol1; }
   * .ordered-list-item.depth2:before { content: counter(ol2, lower-roman) ". "; counter-increment: ol2; }
   * .ordered-list-item.depth3:before { content: counter(ol3, upper-alpha) ". "; counter-increment: ol3; }
   * .ordered-list-item.depth4:before { content: counter(ol4) ". "; counter-increment: ol4; }
   * .list.depth0:first-of-type { counter-reset: ol0; }
   * .list.depth1:first-of-type { counter-reset: ol1; }
   * .list.depth2:first-of-type { counter-reset: ol2; }
   * .list.depth3:first-of-type { counter-reset: ol3; }
   * .list.depth4:first-of-type { counter-reset: ol4; }
   **/
  inlineStyles: (() => {
    const styles: { [index: string]: {} } = {
      BOLD: { style: { fontWeight: "bold" } },
      ITALIC: { style: { fontStyle: "italic" } },
      UNDERLINE: { style: { textDecoration: "underline" } },
      STRIKETHROUGH: { style: { textDecoration: "line-through" } },
    };
    styles["insertLink"] = {
      style: { color: "#0088FF", textDecoration: "underline" },
    };
    return styles;
  })(),

  // this handles converting any inline styles not matched by the inlineStyles map above (custom added styles)
  inlineStyleFn: (style: DraftInlineStyle) => {
    style = customStyleHandler(style) as DraftInlineStyle;
    return (
      style && {
        element: "span",
        style,
      }
    );
  },

  // Converting (rendering) custom block types, like "paragraph" and "horizontal-rule" to html is handled here
  blockRenderers: {
    unstyled: (block: ContentBlock) => {
      let blockStyles: OrderedSet<string> = OrderedSet();
      let blockClasses: OrderedSet<string> = OrderedSet();
      if (classes) blockClasses = blockClasses.add(classes);
      if (styles) blockStyles = blockStyles.add(styles);

      if (block.getLength() === 0) {
        return `<div${getClassesAndStyles({
          block,
          blockStyles,
          classes: blockClasses,
        })}><br></div>`;
      }
      // get block-level styling and classes if any
      // "result" will be the html eventually returned from this function
      const result = `<div${getClassesAndStyles({
        block,
        blockStyles,
        classes: blockClasses,
      })}>${buildHtmlForBlockText("", block, contentState)}</div>`;
      return result;
    },
  },

  blockStyleFn: (block: ContentBlock) => {
    const data = block.getData();
    const attributes: { [index: string]: any } = {};
    let styles: OrderedSet<string> = OrderedSet();
    let classes: OrderedSet<string> = OrderedSet();

    data.forEach((v, k) => {
      if (v === "class") {
        classes = classes.add(k);
      } else if (!["depth", "listStyles", "listStart"].includes(k)) {
        styles = styles.add(`${k}: ${v}`);
      }
    });

    if (classes.size) attributes.class = classes.toArray().join(" ");
    if (styles.size) attributes.style = styles.toArray().join(";");
    return { attributes };
  },

  defaultBlockTag: "div",

  // entityStyleFn: entity => {
  //   const entityType = entity.get('type').toLowerCase();
  //   if (entityType === 'video') {
  //     const { src } = entity.getData();
  //     return {
  //       element: 'video',
  //       attributes: {
  //         src: src,
  //       },
  //     };
  //   }
  // },
});

export function getClassesAndStyles({
  block,
  blockStyles = OrderedSet(),
  classes = OrderedSet(),
}: {
  block: ContentBlock;
  blockStyles?: OrderedSet<string>;
  classes?: OrderedSet<string>;
}) {
  const data = block.getData();
  data
    .filter((v, k) => !["depth", "listStyles", "listStart"].includes(k))
    .forEach((v, k) => {
      if (v === "class") {
        classes = classes.add(k);
      } else {
        blockStyles = blockStyles.add(`${k}: ${v}`);
      }
    });
  const margin = block.get("depth");
  if (margin) {
    blockStyles = OrderedSet.of(`margin-left: ${margin * 2.5}em`).union(
      blockStyles,
    );
  }
  // convert classes & styles to strings and return
  const classesStr =
    (classes.size && ` class="${classes.toArray().join(" ")}"`) || "";
  const blockStylesStr =
    (blockStyles.size && ` style="${blockStyles.toArray().join("; ")}"`) || "";
  return `${classesStr}${blockStylesStr}`;
}

export function buildHtmlForBlockText(
  result: string,
  block: ContentBlock,
  contentState: ContentState,
) {
  if (!block) {
    return "<span>&nbsp;</span>";
  }
  // now build the html for all inline styles for each "styleRange" in the block. A styleRange is
  // any sequence in the block where the characters share the same inline styling.
  block.findStyleRanges(
    () => true,
    (s, e) => {
      let close = "";
      let styles = Map(
        customStyleHandler(block.getInlineStyleAt(s)) as DraftInlineStyle,
      )
        .reduce((styleSet, v, k) => {
          k = kebabCase(k);
          let sv = String(v);
          if (k === "font-size" && /^\d*$/.test(sv)) sv += "pt";
          return styleSet ? styleSet.add(`${k}: ${sv}`) : OrderedSet();
        }, OrderedSet())
        .toArray()
        .join("; ");

      styles = styles ? ` style="${styles}"` : "";
      // If a styleRange overlaps with an "entity" that starts and ends at the same points in the block
      // the entity represents an embeded link
      const startKey = block.getEntityAt(s);
      const endKey = block.getEntityAt(e - 1);
      const entity =
        startKey && startKey === endKey
          ? contentState.getEntity(startKey)
          : null;

      if (styles) {
        result += `<span${styles}>`;
        close = "</span>" + close;
      }
      // Now add the text content of the block for the current styleRange. If a "link" entity exists for this range
      // then wrap the text content in an anchor tag and add the href.
      // The multiple "replace" calls prevent empty paragraphs and extra spaces from collapsing and failing to render.
      // Handle both halfwidth space (U+0020) and fullwidth space (U+3000)
      const rawText = block.getText().slice(s, e);
      let textContent = rawText.replace(/\n/g, "<br>");

      // Replace ALL fullwidth spaces (U+3000) with HTML entity &#12288; to preserve fullwidth display
      // Using &#12288; instead of &nbsp; because &nbsp; is halfwidth space (U+00A0)
      textContent = textContent.replace(
        new RegExp(FULLWIDTH_SPACE_CHAR, "g"),
        FULLWIDTH_SPACE_HTML_ENTITY,
      );

      // Replace multiple consecutive halfwidth spaces (2+) with &nbsp;
      const halfwidthSpaceRegex = new RegExp(`${HALFWIDTH_SPACE}{2,}`, "g");
      textContent = textContent.replace(halfwidthSpaceRegex, (match) => {
        return match
          .split("")
          .map(() => NON_BREAKING_SPACE_HTML_ENTITY)
          .join("");
      });

      // Preserve single halfwidth space at start of this styleRange
      // Check if this is the first styleRange (s === 0) or if previous range ended
      if (
        rawText.length > 0 &&
        rawText[0] === HALFWIDTH_SPACE &&
        textContent[0] === HALFWIDTH_SPACE
      ) {
        textContent = NON_BREAKING_SPACE_HTML_ENTITY + textContent.slice(1);
      }

      // Preserve single halfwidth space at end of this styleRange
      if (
        rawText.length > 0 &&
        rawText[rawText.length - 1] === HALFWIDTH_SPACE &&
        textContent.endsWith(HALFWIDTH_SPACE)
      ) {
        textContent =
          textContent.slice(0, -1) + NON_BREAKING_SPACE_HTML_ENTITY;
      }
      if (entity && entity.getType() === ENTITY_TYPE_LINK) {
        const { url, target } = entity.getData();
        result += `<a href="${getCorrectUrl(url)}" ${
          target ? `target="${target}" rel="noreferrer"` : ""
        }>${textContent}</a>`;
      } else {
        result += textContent;
      }
      result += close;
    },
  );
  return result;
}
